/**
 * Photographic shading for cylindrical bodies (stage units = mm).
 *
 * The body is drawn as thin horizontal slices clipped to the silhouette, so a
 * horizontal gradient always spans the *local* width: a 27 mm neck gets its own
 * dense glass edges instead of a slice of the 63 mm body gradient. A vertical
 * gradient built from the profile slope adds the light caught by shoulders and
 * the shade under the heel.
 *
 * Studio: large softbox high on the left, narrow strip light on the right,
 * dark surroundings, white floor.
 */
import { memo } from 'react';
import { radiusAt, type ContainerModel, type MaterialLook } from './model';

export type Stop = { o: number; c: string; a: number };

export function hex(c: string): [number, number, number] {
  const h = c.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/./g, (x) => x + x) : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/** Mix of two #hex colours, t = 0 → a, 1 → b. Returns #hex (so it can be mixed again). */
export function mix(a: string, b: string, t: number): string {
  const [r1, g1, b1] = hex(a);
  const [r2, g2, b2] = hex(b);
  const f = (x: number, y: number) => Math.max(0, Math.min(255, Math.round(x + (y - x) * t)));
  return `#${[f(r1, r2), f(g1, g2), f(b1, b2)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}
const clamp = (v: number, a = 0, b = 1) => Math.max(a, Math.min(b, v));

export function Grad({ id, stops, vertical = false, user }: { id: string; stops: Stop[]; vertical?: boolean; user?: { y1: number; y2: number } }) {
  const sorted = [...stops].sort((p, q) => p.o - q.o);
  return user ? (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" x2="0" y1={user.y1} y2={user.y2}>
      {sorted.map((s, i) => (
        <stop key={i} offset={clamp(s.o)} stopColor={s.c} stopOpacity={clamp(s.a)} />
      ))}
    </linearGradient>
  ) : (
    <linearGradient id={id} x1="0" x2={vertical ? 0 : 1} y1="0" y2={vertical ? 1 : 0}>
      {sorted.map((s, i) => (
        <stop key={i} offset={clamp(s.o)} stopColor={s.c} stopOpacity={clamp(s.a)} />
      ))}
    </linearGradient>
  );
}

/** Colours of a gradient at full opacity (paired with alphaMask for seam-free transparency). */
export const colourOnly = (stops: Stop[]): Stop[] => stops.map((s) => ({ ...s, a: 1 }));
/** Opacity of a gradient as grey levels for an SVG luminance mask (sRGB in Chromium/WebKit/Gecko). */
export const alphaAsGrey = (stops: Stop[]): Stop[] =>
  stops.map((s) => {
    const g = Math.round(clamp(s.a) * 255).toString(16).padStart(2, '0');
    return { o: s.o, c: `#${g}${g}${g}`, a: 1 };
  });

// ------------------------------------------------------------------ glass
/**
 * Horizontal transmission of a glass cylinder whose wall is `e` (share of the
 * radius) thick. Light passing near the silhouette travels through a long chord
 * of glass and is bent towards the dark studio → dense, dark edges; just inside
 * them the inner surface reflects the softbox → a thin bright line. The centre
 * shows two dark reflections of the studio and is otherwise nearly clear.
 */
export function glassStops(look: MaterialLook, e: number): Stop[] {
  if (look.frosted) {
    // acid-etched glass scatters light: a milky, luminous body, softly greyer at the edges
    const edge = mix(look.edge, '#55625f', 0.35);
    const milk = mix(look.body, '#ffffff', 0.3);
    const w = Math.max(0.05, e * 1.6);
    return [
      { o: 0, c: edge, a: 0.82 },
      { o: w * 0.5, c: mix(edge, milk, 0.5), a: 0.72 },
      { o: w * 1.3, c: milk, a: 0.66 },
      { o: 0.22, c: '#ffffff', a: 0.6 },
      { o: 0.4, c: '#ffffff', a: 0.66 },
      { o: 0.62, c: milk, a: 0.56 },
      { o: 0.84, c: mix(milk, edge, 0.15), a: 0.6 },
      { o: 1 - w * 1.3, c: mix(milk, edge, 0.25), a: 0.66 },
      { o: 1 - w * 0.5, c: mix(edge, milk, 0.4), a: 0.74 },
      { o: 1, c: edge, a: 0.84 },
    ];
  }
  const tinted = look.base === 'tinted';
  const deep = tinted ? mix(look.edge, '#000000', 0.4) : mix(look.edge, '#0b1512', 0.72);
  const body = tinted ? look.body : mix(look.body, look.edge, 0.35);
  const core = tinted ? look.opacity * 0.82 : look.frosted ? 0.5 : 0.05;
  const inner = tinted ? mix(look.body, '#fff3dc', 0.55) : '#ffffff';
  const s: Stop[] = [];
  const side = (left: boolean) => {
    const p = (x: number) => (left ? x : 1 - x);
    const lit = left ? 1 : 0.75;
    s.push({ o: p(0), c: deep, a: tinted ? 0.98 : 0.95 });
    s.push({ o: p(e * 0.35), c: deep, a: tinted ? 0.93 : 0.78 });
    s.push({ o: p(e * 0.75), c: mix(deep, body, 0.5), a: tinted ? 0.85 : 0.34 });
    s.push({ o: p(e * 1.05), c: inner, a: (look.frosted ? 0.25 : 0.62) * lit });
    s.push({ o: p(e * 1.45), c: body, a: core + (tinted ? 0.06 : 0.1) });
    s.push({ o: p(e * 2.6), c: body, a: core + (tinted ? 0.02 : 0.04) });
  };
  side(true);
  side(false);
  // studio reflections seen through the front wall (dark bands) and the clear centre
  s.push({ o: 0.27, c: body, a: core + 0.03 });
  s.push({ o: 0.33, c: deep, a: core + (tinted ? 0.08 : 0.12) });
  s.push({ o: 0.41, c: body, a: core + 0.02 });
  s.push({ o: 0.55, c: body, a: core });
  s.push({ o: 0.66, c: body, a: core + 0.02 });
  s.push({ o: 0.73, c: deep, a: core + (tinted ? 0.06 : 0.09) });
  s.push({ o: 0.8, c: body, a: core + 0.03 });
  return s;
}

// ------------------------------------------------------------------ opaque
/** Lambert cylinder under the softbox; matte coats are flatter, steel is an environment map. */
export function opaqueStops(look: MaterialLook): Stop[] {
  if (look.metal) {
    const b = look.body;
    const d = mix(b, '#1d2124', 0.72);
    const m = mix(b, '#3a3f42', 0.35);
    const l = mix(b, '#ffffff', 0.62);
    const env: [number, string][] = [
      [0, d], [0.05, m], [0.13, l], [0.2, mix(b, '#ffffff', 0.85)], [0.27, l], [0.36, m], [0.44, d],
      [0.52, m], [0.62, b], [0.7, mix(b, '#ffffff', 0.3)], [0.78, m], [0.86, d], [0.93, l], [0.97, m], [1, d],
    ];
    return env.map(([o, c]) => ({ o, c, a: 1 }));
  }
  const dark = look.base === 'dark';
  const white = look.base === 'white';
  if (look.coated) {
    // satin powder coat: a broad, soft gradation from the softbox to the shadow side
    const lit = mix(look.body, '#ffffff', dark ? 0.24 : white ? 0.55 : 0.32);
    const shadow = mix(look.body, dark ? '#000000' : look.edge, dark ? 0.55 : white ? 0.75 : 0.42);
    const out: Stop[] = [];
    for (let i = 0; i <= 20; i++) {
      const x = i / 20;
      const n = Math.asin(clamp(2 * x - 1, -1, 1));
      const key = Math.pow(Math.max(0, Math.cos(n + 0.55)), 0.8);
      const rim = Math.pow(Math.max(0, (x - 0.9) / 0.1), 2) * 0.35 * (1 - Math.pow(Math.max(0, (x - 0.985) / 0.015), 1));
      const edge = Math.pow(Math.abs(2 * x - 1), 5);
      const v = clamp(0.2 + 0.8 * key - 0.35 * edge);
      let c = v > 0.55 ? mix(look.body, lit, (v - 0.55) / 0.45) : mix(shadow, look.body, v / 0.55);
      if (rim > 0) c = mix(c, lit, rim);
      out.push({ o: x, c, a: 1 });
    }
    return out;
  }
  const shade = mix(look.body, dark ? '#000000' : look.edge, dark ? 0.75 : white ? 0.55 : 0.7);
  const out: Stop[] = [];
  for (let i = 0; i <= 20; i++) {
    const x = i / 20;
    const n = Math.asin(clamp(2 * x - 1, -1, 1));
    const key = Math.max(0, Math.cos(n + 0.65));
    const fill = 0.3 * Math.max(0, Math.cos(n - 1.05));
    const edge = Math.pow(Math.abs(2 * x - 1), 7);
    let v = 0.14 + 0.78 * key + fill;
    v = v * (1 - 0.6 * edge) + (x > 0.955 ? 0.3 : 0);
    const flat = dark ? 1 : white ? 0.78 : 0.85;
    v = 0.8 - (0.8 - v) * flat;
    const c = v >= 0.8 ? mix(look.body, '#ffffff', clamp((v - 0.8) * 1.6)) : mix(shade, look.body, clamp(v / 0.8));
    out.push({ o: x, c, a: 1 });
  }
  return out;
}

/** Liquid seen through glass: a lens of colour, darker and richer at the sides. */
export function liquidStops(tint: string): Stop[] {
  const deep = mix(tint, '#1a0c02', 0.55);
  const glow = mix(tint, '#fff4dc', 0.45);
  return [
    { o: 0, c: deep, a: 0.97 },
    { o: 0.08, c: mix(deep, tint, 0.5), a: 0.93 },
    { o: 0.22, c: tint, a: 0.86 },
    { o: 0.42, c: glow, a: 0.74 },
    { o: 0.58, c: tint, a: 0.8 },
    { o: 0.8, c: mix(deep, tint, 0.4), a: 0.9 },
    { o: 1, c: deep, a: 0.97 },
  ];
}

// ------------------------------------------------------------------ geometry helpers
export interface Slice {
  y0: number;
  y1: number;
  r: number;
}
/** Horizontal slices of the body: ≤ 1.6 mm, and thin enough that the radius changes ≤ 0.2 mm per slice. */
export function slices(c: ContainerModel, step = 1.6): Slice[] {
  const H = c.heightMm;
  const p = c.profile;
  const out: Slice[] = [];
  let y = 0;
  while (y < H - 1e-6) {
    const slope = Math.max(Math.abs(slopeAt(c, y)), Math.abs(slopeAt(c, y + 0.25)));
    const h = Math.max(0.2, Math.min(step, 0.2 / Math.max(slope, 1e-3)));
    const y1 = Math.min(H, y + h);
    out.push({ y0: y, y1, r: Math.max(radiusAt(p, y), radiusAt(p, y1), radiusAt(p, (y + y1) / 2)) });
    y = y1;
  }
  return out;
}

/** Slope of the outline (dr/dy) at y; positive on shoulders, negative at the heel. */
export function slopeAt(c: ContainerModel, y: number): number {
  const d = 0.8;
  return (radiusAt(c.profile, y + d) - radiusAt(c.profile, y - d)) / (2 * d);
}

/**
 * Vertical light from the profile: shoulders facing up catch the softbox,
 * the heel turns away into shade. Returns stops for userSpaceOnUse y ∈ [0, H].
 */
export function slopeStops(c: ContainerModel, kind: 'light' | 'shade', strength: number, colour: string): Stop[] {
  const H = c.heightMm;
  const out: Stop[] = [];
  const n = Math.max(40, Math.round(H / 2));
  for (let i = 0; i <= n; i++) {
    const y = (H * i) / n;
    const s = slopeAt(c, y);
    const ang = Math.atan(s); // >0 facing up
    const v = kind === 'light' ? Math.max(0, Math.sin(ang)) : Math.max(0, -Math.sin(ang));
    out.push({ o: y / H, c: colour, a: Math.pow(v, kind === 'light' ? 1.3 : 0.9) * strength });
  }
  return out;
}

interface SliceStackProps {
  c: ContainerModel;
  clipId: string;
  fill: (s: Slice) => string;
  extendBottom: number;
  /** Changes whenever `fill` would return different values (memo key). */
  fillKey: string;
}
/**
 * Slice stack clipped to the silhouette, each slice filled by a gradient chosen per slice.
 * Memoised: hundreds of rects must not re-render on every turntable frame.
 */
export const SliceStack = memo(SliceStackImpl, (a, b) => a.c === b.c && a.clipId === b.clipId && a.extendBottom === b.extendBottom && a.fillKey === b.fillKey);
function SliceStackImpl({ c, clipId, fill, extendBottom }: SliceStackProps) {
  const sl = slices(c);
  return (
    <g clipPath={`url(#${clipId})`}>
      {sl.map((s, i) => {
        // slices overlap a little: fills are opaque (alpha comes from a mask), so no seams
        const top = i === 0 ? -extendBottom : s.y0 - 0.4;
        const bot = i === sl.length - 1 ? s.y1 + extendBottom : s.y1 + 0.4;
        return <rect key={i} x={-s.r} y={top} width={2 * s.r} height={bot - top} fill={fill(s)} />;
      })}
    </g>
  );
}
